import classNames from "classnames";
import _ from 'lodash';
import React from 'react';
import ToolTip, { CRITERIA_PATENT_COUNT, EVENT_PATIENT_COUNT, INDEX_EVENT_PATIENT_COUNT } from '../../components/common/ToolTip';
import { isDefined } from '../../reducers/util';
import { Tracker } from "../../types/Project";
import { Criterion, Group, Period } from "../HealthcareUsageReport/types/CohortQuery";
import styles from './CohortSummary.module.scss';
import { operatorDescription, periodDescription } from './CohortUtil';
import { TimeBetweenEvents } from "./TimeBetweenEvents/TimeBetweenEvents";
import CriterionDescription from "./CriterionDescription";

const isInclusion = (criteria: Criterion[], index: number) => {
  const previous = criteria[index - 1]
  return (index === 0) || !previous.operator.includes("not")
}

const criteriaDescription = (criteria: Criterion[], tracker: Tracker[]) => {
  const result = criteria
      .flatMap((criterion, index) => {
        const criteriaPatientCount = findPatientCount(criterion.id, tracker)
        return [
          <div className={styles['criterion-wrapper']} key={criterion.id}>
            <div
                className={classNames(
                    styles['criterion'],
                    isInclusion(criteria, index) ? styles["inclusion"] : styles["exclusion"]
                )}>
              <CriterionDescription criterion={criterion} />
              {_.isNil(criteriaPatientCount) ?
                  null :
                  <span className={styles["criteria-patient-count"]} data-testid="suppressed">
                {"Criteria Patient Count: " + findPatientCount(criterion.id, tracker)}
                    <ToolTip content={CRITERIA_PATENT_COUNT} />
              </span>}
            </div>
          </div>,
          <div className={styles["operator"]} key={criterion.id + "_"}>
            {operatorDescription(criterion.operator)}
          </div>,
        ]
      })
  result.pop()
  return result
}

const groupDescription = (groups: Group[], tracker: Tracker[]) => {
  const result = groups
      .flatMap(function callback(i: Group, index: number, array: Group[]) {
        const isIndex = isDefined(i.index) && i.index && index === 0
        const patientCount = isDefined(tracker) ? (isIndex ? "Index" : "") + " Event Patient Count: " + findPatientCount(i.id, tracker) : ""
        const operator = <div className={styles["group-operator"]} key={i.id + "_"}>
          {operatorDescription(i.operator)}
        </div>
        return [
          <div className={styles.event} key={i.id} data-testid={`group-${i.id}`}>
            <div className={styles['event-title']}>
              <span className={styles['event-name-prefix']}>Event: </span>
              <span className={styles['event-name']}>
              {i.title} <span className={styles["patient-count"]} data-testid="suppressed">{patientCount}<ToolTip content={isDefined(i.index) && i.index ? INDEX_EVENT_PATIENT_COUNT : EVENT_PATIENT_COUNT} /></span>
            </span>
              {isIndex ? <span className={styles['event-index']}>INDEX</span> : ""}
            </div>
            {criteriaDescription(i.criteria, tracker)}
          </div>,
          isIndex ? [<TimeBetweenEvents key={i.id + "_tbe"} id={i.id} operator={i.operator} trackers={tracker}/>, operator] : operator
        ]
      })
  result.pop()
  return result
}

const findPatientCount = (id: string, tracker: Tracker[]) => {
  if (isDefined(tracker)) {
    const match = tracker.find(i => i.description === id)
    if (isDefined(match) && isDefined(match.result)) {
      return parseInt(match.result).toLocaleString()
    } else {
      return null
    }
  } else {
    return null
  }
}

export type CohortSummaryProps = {
  label: string,
  period: Period,
  groups: Group[],
  count: string,
  tracker: Tracker[],
}

export const CohortSummary = ({ label, period, groups, count, tracker }: CohortSummaryProps) => {
  const patientCount = isDefined(count) ? <span> with <span style={{ fontWeight: 'bold', color: 'black' }}>{parseInt(count).toLocaleString()}</span> patients</span> : ""
  return <div className={styles['cohort-summary']}>
    <div className={styles['cohort-title']} data-testid={"cohort-title"}>
      <strong>Cohort Breakdown</strong>
      <br />
      For {label}{patientCount}.<br />
      {periodDescription(period)}
    </div>
    {groupDescription(groups, tracker)}
  </div>
}